// Pantalla intermedia de los atajos de "Ediciones rápidas" —
// ADR-2026-08-20-los-atajos-tienen-pantalla-propia-para-elegir-imagen.
//
// Cuatro de los siete atajos (Editar, Recortar, Mejorar, Descripción) navegaban al
// historial y ahí se terminaba: el usuario tocaba "Mejorar" y aterrizaba en una grilla
// de creaciones sin ninguna señal de qué había pedido.
//
// El layout es EL MISMO que el del modo Carrusel, a pedido explícito
// (ADR-2026-08-20-la-pantalla-de-atajos-copia-el-layout-del-carrusel) — y sigue siéndolo
// después de que Carrusel se mudó al diseño de los modos
// (ADR-2026-09-07-los-atajos-toman-el-diseno-de-los-modos): en desktop, el mismo
// `ShellModoRedisenado` con la grilla en el panel de la IZQUIERDA y la carátula del atajo
// a la derecha; en mobile, carátula full-bleed con panel blanco redondeado abajo y la
// grilla dentro de un bottom sheet, sin cambios.
//
// SÓLO "elegir una ya generada" en los atajos de siempre: los efectos que corren en el
// backend piden el `id` de una fila de `imagenes_generadas` —`/v1/images/upscale` y
// `/v1/images/instagram-description` con `image_id: uuid`, `/v1/images/edit` con
// `imagen_id: uuid`— y una foto subida no tiene fila. Por eso esos cuatro no tienen el
// botón azul "Subir imagen" que el carrusel sí tiene: sería prometer algo que corta al
// final del flujo.
//
// La EXCEPCIÓN es "Expansión mágica" (`conSubirPropia`), cuyo backend aprendió a
// aceptar una referencia de storage además del id de fila — el mismo contrato
// bucket+path del carrusel. Ese atajo, elegida la fuente (generada o subida), pasa a un
// SEGUNDO PASO a pantalla completa en este mismo layout (como el "configurar" del
// carrusel — pedido explícito: acá no va el sheet flotante del visor): el hero muestra
// el preview del encuadre nuevo y la card la elección de formato. Al expandir navega a
// la galería de recién generadas (`GeneratedImagesInline`), igual que el carrusel.
// Ver ADR-2026-08-25-expansion-magica-atajo-home y
// ADR-2026-08-25-expansion-atajo-configura-en-pantalla.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Check,
  ChevronRight,
  Copy,
  Crop,
  Download,
  Expand,
  ImageUp,
  Info,
  Instagram,
  Loader2,
  Maximize,
  Pencil,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useSidebar } from '../../../context/SidebarContext';
import { useVolverAtras } from '../../../hooks/useVolverAtras';
// La vuelta atrás del modo viaja en la entrada de historial, no en `sessionStorage`
// (ADR-2026-09-10-los-modos-con-ruta-propia-tambien-vuelven-a-lo-que-generaste).
import {
  configDelModoAlMontar,
  recordarConfigDelModo,
} from './shared/volverAlModo';
import { useDebajoDeMd } from '../../../hooks/useEsMobile';
import { mensajeDeUpscaleFallido, postImageUpscale } from '../../../lib/imageEdit';
import {
  esperarPostDescription,
  postInstagramDescription,
} from '../../../lib/instagramDescription';
import { setCachedSignedUrl } from '../../../utils/imageCache';
import { descargarArchivo } from '../../../utils/descargarArchivo';
import { marcarDescargadas } from '../../../utils/marcarDescargada';
import { FondoCarga, NucleoCarga } from '../../../components/loading/NucleoCarga';
import { useGenerationProgress } from '../../../components/loading/useGenerationProgress';
import OptionSheet from '../../../components/opciones/OptionSheet';
import SubscriptionLimitModal from '../../../components/SubscriptionLimitModal';
import { GenerateAdError } from '../../../lib/generateAdRequest';
import { subirImagenParaAnimar } from '../../../lib/animarImagenUpload';
import {
  CREDITOS_EXPANSION,
  encuadreOriginal,
  expandirImagen,
  ladoQueCrece,
  opcionesParaImagen,
  type RatioExpansion,
} from '../../../lib/expansionMagica';
import {
  GeneradasPickGrid,
  type GeneradaPick,
} from './shared/GeneradasPickGrid';
import {
  GeneratedImagesHistory,
  type AccionVisor,
} from '../../GeneratedImagesHistory';
import { ShellModoRedisenado } from './shared/ShellModoRedisenado';
import { PasoUnoMobile } from './shared/PasoUnoMobile';
import {
  ImageCropCanvas,
  ImageCropControls,
  useCropState,
} from '../../../components/ImageCropModal';
import { CardPicker } from '../components/PanelCatalogoCompacto';
import { PanelLateralModo } from '../components/PanelLateralModo';

/** Los atajos que pasan por acá. El slug es el `:accion` de la URL. */
export type SlugAtajo =
  | 'editar'
  | 'recortar'
  | 'mejorar'
  | 'descripcion'
  | 'expansion';

interface ConfigAtajo {
  /**
   * Cómo se llama el atajo. Encabeza la pantalla (breadcrumb + `<h1>`) y es el MISMO
   * `label` con el que aparece en "Ediciones rápidas" de la home — un solo nombre por
   * cosa, igual que los modos (ADR-2026-08-03-un-solo-nombre-por-modo).
   */
  nombre: string;
  /** Título del bloque de la card, en imperativo: es la consigna del paso. */
  titulo: string;
  /** Qué va a pasar con la imagen elegida. */
  bajada: string;
  /**
   * Título del bloque que ENCABEZA la muestra en desktop — el mismo tratamiento de
   * Modelo v2 y Catálogo (ADR-2026-08-21-modelo-v2-la-muestra-se-queda): título en
   * negrita arriba de la carátula y `heroDesc` como bajada corta debajo, para que se
   * lea como el título de lo que el modo hace y no como el epígrafe de una foto.
   */
  heroTitulo: string;
  /** Copy del hero (desktop): la bajada del título, de una línea. */
  heroDesc: string;
  Icono: LucideIcon;
  /**
   * Carátula del atajo. `null` = se dibuja el ícono grande en su lugar.
   *
   * Editar tiene la suya desde siempre (`editar_prompt.png`, el antes/después con la
   * flecha). Los otros cuatro APUNTAN a su archivo (`/images/modo_<atajo>/caratula.png`)
   * aunque todavía no exista: si la imagen no carga se cae al ícono, así que dejar el
   * archivo en esa carpeta alcanza para que aparezca, sin tocar código
   * (ADR-2026-09-07-la-caratula-del-atajo-se-cae-al-icono).
   */
  hero: string | null;
  /** Texto del vacío de la grilla, nombrando lo que este atajo hace. */
  emptyHint: string;
  /**
   * Muestra el botón azul "Subir imagen" (el del carrusel). Sólo lo declara la
   * expansión: es el único efecto cuyo backend acepta una foto sin fila.
   */
  conSubirPropia?: boolean;
  /**
   * Cómo se aplica el efecto:
   *  - `visor`: se monta el visor del historial (`soloVisor`) con la acción disparada.
   *    Son los que NO tienen pantalla propia y viven adentro de ese componente.
   *  - `ruta`: el efecto ya tiene su pantalla; se navega con el id de la imagen. Con
   *    `enDesktop`, en escritorio se abre el visor con esa acción en vez de la ruta.
   *  - `expansion`: segundo paso EN ESTA PANTALLA (preview del encuadre en el hero +
   *    formatos en la card, como el "configurar" del carrusel); al expandir se navega
   *    a la galería de recién generadas.
   *  - `recorte`: segundo paso EN ESTA PANTALLA, en el layout de los modos — la imagen
   *    con sus manijas en la columna de la derecha y los controles en el panel
   *    (ADR-2026-09-07-el-recorte-se-hace-en-el-panel-del-modo). Antes era un `visor`.
   *  - `upscale`: igual que `recorte`, para "Mejorar resolución" — la escala y el
   *    resultado en el panel, la imagen a la derecha
   *    (ADR-2026-09-07-mejorar-resolucion-se-hace-en-el-panel-del-modo).
   *  - `descripcion`: igual, para "Descripción" — el texto se escribe en el panel de la
   *    izquierda y la foto descrita se queda a la derecha
   *    (ADR-2026-09-08-la-descripcion-se-escribe-en-el-panel-del-modo).
   *
   * Los tres últimos son SÓLO de escritorio: su paso 2 es el layout de dos columnas, que
   * abajo de `md` no existe. En mobile siguen abriendo el visor, que es donde esas
   * herramientas ya tenían su versión de teléfono — de ahí el `enMobile`.
   */
  destino:
    | { tipo: 'visor'; accion: AccionVisor }
    | {
        tipo: 'ruta';
        armar: (imageId: string, companyId: string) => string;
        /** En escritorio, en vez de la ruta, el visor con esta acción ya disparada. */
        enDesktop?: AccionVisor;
      }
    | { tipo: 'expansion' }
    | { tipo: 'recorte'; enMobile: AccionVisor }
    | { tipo: 'upscale'; enMobile: AccionVisor }
    | { tipo: 'descripcion'; enMobile: AccionVisor };
}

/** La imagen a expandir: una fila generada o una foto subida. `url` es sólo el preview. */
interface FuenteExpansion {
  imagenId?: string;
  subida?: { bucket: string; path: string };
  url: string;
}

/** Lo que hace falta para reabrir el atajo de expansión tal como se generó —
 *  ADR-2026-09-10-los-modos-con-ruta-propia-tambien-vuelven-a-lo-que-generaste.
 *  Las MEDIDAS no van: se vuelven a sacar de la imagen con el preloader de siempre. */
interface ConfigDeExpansion {
  fuente: FuenteExpansion;
  ratio: RatioExpansion | null;
}

const ATAJOS: Record<SlugAtajo, ConfigAtajo> = {
  editar: {
    nombre: 'Editar',
    titulo: 'Elegí la imagen a editar',
    bajada: 'Después le pedís el cambio con un prompt y Atlas lo aplica.',
    heroTitulo: 'Pedile el cambio en palabras',
    heroDesc:
      'Otro fondo, otro color, sacar algo — Atlas lo resuelve sobre tu foto.',
    Icono: Pencil,
    hero: '/images/editar_prompt.png',
    emptyHint: 'Todavía no generaste ninguna imagen. Generá una y volvé para editarla.',
    // En escritorio, el visor con la barra de Editar abierta: es la misma edición (adjuntos,
    // escritor, versiones) sobre la imagen ampliada, con el panel de herramientas a mano
    // (ADR-2026-10-07-el-atajo-editar-abre-el-visor). En el celular, la pantalla propia,
    // que recibe la imagen por query params (`EditarImagen` lee `?id=` y `?companyId=`).
    destino: {
      tipo: 'ruta',
      armar: (imageId, companyId) =>
        `/redes-sociales/editar-imagen?id=${encodeURIComponent(imageId)}&companyId=${encodeURIComponent(companyId)}`,
      enDesktop: 'editar',
    },
  },
  recortar: {
    nombre: 'Recortar',
    titulo: 'Elegí la imagen a recortar',
    bajada: 'Después ajustás el encuadre y la descargás.',
    heroTitulo: 'Ajustá el encuadre',
    heroDesc: 'Recortá a la proporción que pide cada red, sin salir de Atlas.',
    Icono: Crop,
    hero: '/images/modo_recortar/caratula.png',
    emptyHint: 'Todavía no generaste ninguna imagen. Generá una y volvé para recortarla.',
    // El recorte se resuelve en ESTA pantalla, no abriendo el visor full-screen.
    destino: { tipo: 'recorte', enMobile: 'recortar' },
  },
  mejorar: {
    nombre: 'Mejorar',
    titulo: 'Elegí la imagen a mejorar',
    bajada: 'Atlas le sube la resolución con IA, sin perder detalle.',
    heroTitulo: 'Subí la resolución con IA',
    heroDesc: 'Sirve para imprimir o para publicarla en grande, sin perder detalle.',
    Icono: Maximize,
    hero: '/images/modo_mejorar/caratula.png',
    emptyHint: 'Todavía no generaste ninguna imagen. Generá una y volvé para mejorarla.',
    // Como el recorte: la escala y el resultado en el panel, la imagen a la derecha.
    destino: { tipo: 'upscale', enMobile: 'mejorar' },
  },
  descripcion: {
    nombre: 'Descripción',
    titulo: 'Elegí la imagen a publicar',
    bajada: 'Atlas te escribe el texto del post a partir de la foto.',
    heroTitulo: 'El texto del post lo escribe Atlas',
    heroDesc: 'Mira la imagen y arma la descripción, con hashtags incluidos.',
    Icono: Instagram,
    hero: '/images/modo_descripcion/caratula.png',
    emptyHint:
      'Todavía no generaste ninguna imagen. Generá una y volvé para escribir su descripción.',
    // El texto se escribe en el panel, con la foto al lado. El visor —la imagen sobre
    // el velo negro con la descripción encima— sigue siendo el camino de MOBILE y el de
    // quien pide la descripción desde una imagen ya abierta; acá cambia sólo el modo.
    destino: { tipo: 'descripcion', enMobile: 'descripcion' },
  },
  expansion: {
    nombre: 'Expandir',
    titulo: 'Elegí la imagen a expandir',
    bajada: 'Después elegís el formato y la IA pinta lo que falta alrededor.',
    heroTitulo: 'Llevá la foto a otro encuadre',
    heroDesc: 'La imagen queda intacta y la IA pinta el lienzo nuevo alrededor.',
    Icono: Expand,
    hero: '/images/modo_expandir/caratula.png',
    emptyHint: 'Todavía no generaste ninguna imagen. Subí una foto para expandirla.',
    // Único atajo con foto propia: su backend acepta bucket+path (ver arriba).
    conSubirPropia: true,
    destino: { tipo: 'expansion' },
  },
};

/**
 * El recorte, montado en el layout de los modos — ADR-2026-09-07-el-recorte-se-hace-en-el-panel-del-modo.
 *
 * La imagen con sus manijas ocupa la columna de la derecha y los controles —medidas,
 * proporciones, Descargar— van en el panel de la izquierda, igual que el resto de las
 * pantallas del shell. Antes esto abría el visor del historial a pantalla completa, con
 * su velo negro: un contexto nuevo para una tarea que ya estaba en curso.
 *
 * Es un componente aparte y no una rama de `AtajoImagenMode` porque `useCropState` es un
 * hook: con la lógica adentro habría que llamarlo también cuando todavía no hay imagen
 * elegida —o sea, con la URL vacía, que su efecto trata como una carga fallida—.
 */
const RecorteEnElPanel: React.FC<{
  pick: GeneradaPick;
  /** Cómo se llama el atajo: encabeza la pantalla. */
  nombre: string;
  sidebarWidth: number;
  /** Volver a elegir imagen. Es el "Cancelar" de los controles. */
  onVolver: () => void;
  onInicio: () => void;
}> = ({ pick, nombre, sidebarWidth, onVolver, onInicio }) => {
  // La grilla ya entrega la URL de la versión `full`, que es de la que hay que recortar:
  // con la `opt` la descarga saldría del tamaño de la miniatura.
  const cropState = useCropState(pick.signedUrl, pick.fileName, onVolver);

  return (
    <ShellModoRedisenado
      sidebarWidth={sidebarWidth}
      migas={[{ label: 'Inicio', onClick: onInicio }, { label: nombre }]}
      titulo={nombre}
      panel={
        <>
          <div className="shrink-0 px-6 flex justify-between gap-2 items-baseline pt-5 pb-1">
            <p className="text-[17px] font-semibold text-brand-black leading-tight">
              Seleccioná el área que quieras recortar
            </p>
          </div>
          {/* Los controles traen su propio pie (Descargar + Cancelar) y miden `h-full`,
              así que se les da el alto y no un scroller: adentro ya se reparten. La
              variante `panel` es la de este ancho: sin título propio —lo dicen el
              breadcrumb y el `<h1>` de arriba—, proporciones repartidas a todo el ancho
              y la resolución final debajo de ellas. */}
          <div className="flex-1 min-h-0">
            <ImageCropControls
              cropState={cropState}
              onCancel={onVolver}
              variant="panel"
            />
          </div>
        </>
      }
      muestra={
        // El canvas se estira al contenedor, así que la columna le da la caja. El
        // redondeo va acá: el canvas pinta hasta el borde y sin esto queda en ángulo
        // recto contra el resto de la pantalla, que es todo esquinas de 16px.
        <div className="flex-1 min-h-0 w-full overflow-hidden rounded-2xl animate-paso-adelante">
          <ImageCropCanvas cropState={cropState} />
        </div>
      }
    />
  );
};

/**
 * La espera del upscale, SOBRE la imagen — ADR-2026-09-07-la-espera-del-upscale-va-sobre-la-imagen.
 *
 * Es la misma pantalla de carga que la app usa para toda generación —el azul `#3f5bd6`
 * con la foto de fondo, las estrellas y la barra blanca con relleno `#1644EB`
 * (`NucleoCarga`, ADR-2026-09-06-nucleo-de-carga-compartido)—, pero recortada al marco
 * de la imagen en vez de tapar la pantalla entera. En producción esto era un overlay
 * full-screen: el usuario perdía de vista lo que estaba mejorando.
 *
 * Componente propio para que el motor de la barra (`useGenerationProgress`, RAF) NAZCA
 * con la espera: montado siempre, habría que arrancarlo y pararlo por fuera, y el
 * progreso quedaría vivo entre una mejora y la siguiente.
 *
 * `listo` no la desmonta: la barra salta a 100%, y recién ahí `onCompleto` la baja y
 * aparece la imagen final. Una barra que desaparece en 60% deja la sensación de que se
 * cortó algo.
 */
const CapaMejorando: React.FC<{
  /** El trabajo terminó (con o sin éxito): la barra completa. */
  listo: boolean;
  /** La barra llegó a 100%. */
  onCompleto: () => void;
}> = ({ listo, onCompleto }) => {
  const progreso = useGenerationProgress({
    // Lo mismo que le pasaba el visor a su pantalla de carga: el upscale no reporta
    // progreso, así que la barra es falsa y capea en 97% hasta que llega `completeNow`.
    durationMs: 60_000,
    holdMs: 0,
    indeterminate: false,
    completeNow: listo,
    completeAtEnd: false,
    onDone: onCompleto,
  });
  return (
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center overflow-hidden rounded-2xl bg-[#3f5bd6] px-6 py-8 text-white">
      <FondoCarga />
      <div className="relative z-10 flex min-h-0 w-full flex-1 flex-col items-center justify-center">
        <NucleoCarga
          titulo="Mejorando resolución…"
          subtitulo="Esto puede tardar hasta 1 minuto"
          progreso={progreso}
        />
      </div>
    </div>
  );
};

/**
 * "Mejorar resolución", montado en el layout de los modos —
 * ADR-2026-09-07-mejorar-resolucion-se-hace-en-el-panel-del-modo.
 *
 * Mismo trato que el recorte: la imagen elegida ocupa la columna de la derecha y la
 * configuración —la escala, la resolución que va a salir, el costo— vive en el panel de
 * la izquierda. Antes esto abría el visor del historial a pantalla completa y ahí un
 * panel flotante encima: dos capas nuevas para una tarea que ya estaba en curso.
 *
 * Se queda en la pantalla DESPUÉS de mejorar, en vez de mandar a otro lado: el resultado
 * aparece en la misma columna, con un toggle para compararlo contra el original y el
 * botón de descarga en el pie. Es lo que el visor prometía con su toast ("usá el toggle
 * para comparar") y que acá se puede mostrar sin mudar al usuario de contexto.
 *
 * Componente aparte y no una rama de `AtajoImagenMode` por lo mismo que el recorte: todo
 * su estado (escala, progreso, resultado) sólo existe con una imagen elegida, y montarlo
 * acá lo hace nacer y morir con ella.
 */
const MejorarEnElPanel: React.FC<{
  pick: GeneradaPick;
  /** Cómo se llama el atajo: encabeza la pantalla. */
  nombre: string;
  companyId: string | null;
  sidebarWidth: number;
  /** Volver a elegir imagen. Es el "Cancelar" del pie. */
  onVolver: () => void;
  onInicio: () => void;
}> = ({ pick, nombre, companyId, sidebarWidth, onVolver, onInicio }) => {
  const [escala, setEscala] = useState<2 | 4>(2);
  const [procesando, setProcesando] = useState(false);
  /**
   * La capa de espera está arriba de la imagen. Dura MÁS que el pedido: se levanta
   * cuando la barra termina de llegar a 100%, no cuando vuelve el backend.
   */
  const [mostrandoEspera, setMostrandoEspera] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** URL firmada de la versión mejorada. Con esto puesto, la pantalla está en resultado. */
  const [mejorada, setMejorada] = useState<string | null>(null);
  /** Qué se está mirando en la columna de la derecha. Arranca en la original: es la
   *  única que existe hasta que la mejora vuelve. */
  const [viendo, setViendo] = useState<'original' | 'mejorada'>('original');
  const [limitModal, setLimitModal] = useState<
    'free-trial-ended' | 'plan-limit-reached' | null
  >(null);
  /** Medidas REALES del original: la resolución final es la de la escala elegida. */
  const [medidas, setMedidas] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  const costo = escala === 2 ? 1 : 2;
  /** Con resultado y el toggle en "mejorada". Antes de eso, siempre la original. */
  const mostrandoMejorada = mejorada !== null && viendo === 'mejorada';

  /**
   * El marco de la imagen dentro de la columna, EN PIXELES.
   *
   * Se calcula en vez de dejárselo a `max-h-full max-w-full` porque la capa de espera se
   * posiciona contra él: con la imagen suelta en la columna, el `<div>` que la envuelve
   * mide lo que mide el contenido y esos `max-*` (que son porcentajes) no tienen contra
   * qué resolver —la imagen salía a tamaño natural y la capa caía corrida—. Teniendo las
   * medidas reales, el encaje es una cuenta: el menor de los dos factores.
   *
   * La mejorada tiene la MISMA proporción (es la original por N), así que el marco no
   * cambia al aparecer el resultado.
   */
  const columnaRef = useRef<HTMLDivElement | null>(null);
  const [caja, setCaja] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = columnaRef.current;
    if (!el || medidas.w === 0 || medidas.h === 0) return;
    const medir = () => {
      const r = el.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) return;
      const factor = Math.min(r.width / medidas.w, r.height / medidas.h);
      setCaja({
        w: Math.round(medidas.w * factor),
        h: Math.round(medidas.h * factor),
      });
    };
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, [medidas.w, medidas.h]);

  // Las medidas salen de un preloader y no del `onLoad` del <img>: la imagen puede estar
  // en caché y su `load` dispara antes de que React enganche el handler — `complete`
  // cubre ese caso. La misma jugada que el paso de formato de la expansión.
  useEffect(() => {
    let cancelado = false;
    const medir = (img: HTMLImageElement) => {
      if (!cancelado && img.naturalWidth > 0) {
        setMedidas({ w: img.naturalWidth, h: img.naturalHeight });
      }
    };
    const img = new Image();
    img.onload = () => medir(img);
    img.src = pick.signedUrl;
    if (img.complete) medir(img);
    return () => {
      cancelado = true;
      img.onload = null;
    };
  }, [pick.signedUrl]);

  const mejorar = async () => {
    if (procesando) return;
    setProcesando(true);
    setMostrandoEspera(true);
    setError(null);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('No pudimos identificar tu cuenta. Recargá la página.');
      const data = await postImageUpscale({
        user_id: user.id,
        image_id: pick.id,
        scale: escala,
      });
      if (data.success) {
        // El backend devuelve el path, no la URL: hay que firmarlo. El fallback es la
        // convención de siempre (`<empresa>/upscaled/<archivo>`), la misma que usa el
        // visor cuando la respuesta no lo trae.
        const path = data.upscaled_path || `${companyId}/upscaled/${pick.fileName}`;
        const { data: firmada } = await supabase.storage
          .from('generadas')
          .createSignedUrl(path, 3600);
        if (firmada?.signedUrl) {
          setCachedSignedUrl(path, firmada.signedUrl);
          setMejorada(firmada.signedUrl);
          setViendo('mejorada');
        } else {
          // La mejora SE HIZO —y se cobró—: decir "error" acá sería mentir. Queda
          // guardada en la imagen, que es de donde el historial la va a levantar.
          setError(
            'La imagen se mejoró, pero no pudimos traerla para mostrarla. Está guardada en tus creaciones.',
          );
        }
      } else if (data.error === 'insufficient_credits') {
        const { data: perfil } = await supabase
          .from('company_profiles')
          .select('plan')
          .eq('id_company', companyId)
          .single();
        setLimitModal(
          (perfil as { plan?: string } | null)?.plan === 'gratis'
            ? 'free-trial-ended'
            : 'plan-limit-reached',
        );
      } else {
        setError(mensajeDeUpscaleFallido(data));
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'No pudimos procesar la imagen. Intentá nuevamente en unos minutos.',
      );
    } finally {
      try {
        window.dispatchEvent(new Event('atlas:credits-updated'));
      } catch {
        /* sin listeners no pasa nada */
      }
      setProcesando(false);
    }
  };

  const descargar = async () => {
    if (!mejorada) return;
    // `<nombre>_mejorada.<ext>`, con la misma forma que el `_recortada` del recorte.
    const punto = pick.fileName.lastIndexOf('.');
    const nombreArchivo =
      punto > 0
        ? `${pick.fileName.slice(0, punto)}_mejorada${pick.fileName.slice(punto)}`
        : `${pick.fileName}_mejorada.png`;
    try {
      const bajo = await descargarArchivo({ url: mejorada, fileName: nombreArchivo });
      // La mejorada es la misma creación en HD (`upscaled_path` de la fila): bajarla es llevársela.
      if (bajo) void marcarDescargadas([pick.id], companyId);
    } catch {
      setError('No pudimos descargar la imagen. Intentá de nuevo.');
    }
  };

  /** Rótulo de sección, el mismo que usan los controles del recorte en el panel. */
  const rotulo = (texto: string) => (
    <p className="text-[13px] font-semibold text-brand-black/50 uppercase tracking-wide mb-2">
      {texto}
    </p>
  );

  return (
    <>
      <ShellModoRedisenado
        sidebarWidth={sidebarWidth}
        migas={[{ label: 'Inicio', onClick: onInicio }, { label: nombre }]}
        titulo={nombre}
        panel={
          <>
            <div className="shrink-0 px-6 flex justify-between gap-2 items-baseline pt-5 pb-1">
              <p className="text-[17px] font-semibold text-brand-black leading-tight">
                {mejorada
                  ? 'Listo: ya tenés tu imagen en alta'
                  : 'Elegí cuánto querés agrandarla'}
              </p>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-3 flex flex-col gap-5">
              {mejorada ? (
                <p className="text-[13.5px] text-[#3C3C43]/60 leading-relaxed">
                  La imagen quedó a {escala} veces su resolución original. Compará las dos
                  versiones con el selector de abajo: la mejorada queda guardada en la
                  misma creación, así que también la vas a encontrar en tu historial.
                </p>
              ) : (
                <>
                  <div>
                    {rotulo('Escala')}
                    {/* Las dos opciones se reparten la fila, como las proporciones del
                        recorte: son la misma clase de elección y este panel tiene ancho
                        de sobra para las dos. */}
                    <div className="flex items-stretch gap-1.5">
                      {([2, 4] as const).map((n) => (
                        <button
                          key={n}
                          type="button"
                          disabled={procesando}
                          onClick={() => setEscala(n)}
                          className={`flex-1 min-w-0 h-10 rounded-md text-[14px] font-medium transition-colors disabled:cursor-not-allowed ${
                            escala === n
                              ? 'bg-brand-primary text-white'
                              : 'bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated'
                          }`}
                        >
                          {n}x
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Lo que se va a llevar, en números: la resolución de salida. Es el
                      mismo bloque —y el mismo gris neutro, que no tira a celeste— con el
                      que el recorte muestra la suya. */}
                  {medidas.w > 0 && (
                    <div>
                      {rotulo('Resolución final')}
                      <div className="flex items-center justify-center py-2.5 rounded-md bg-black/[0.06]">
                        <span className="text-[13px] font-medium text-neutral-600">
                          {medidas.w * escala} × {medidas.h * escala} px
                        </span>
                      </div>
                    </div>
                  )}

                  <div>
                    {rotulo('Información')}
                    <div className="rounded-[13px] bg-white overflow-hidden border border-neutral-200">
                      <div className="px-4 py-[11px]">
                        <div className="flex items-center gap-3 mb-2">
                          <Maximize className="w-[18px] h-[18px] text-brand-primary shrink-0" />
                          <span className="text-[15px] text-brand-black/90">
                            Resolución {escala}x
                          </span>
                        </div>
                        <p className="text-[13px] text-[#3C3C43]/60">
                          Atlas reconstruye el detalle con IA: sirve para imprimir o para
                          publicarla en grande.
                        </p>
                      </div>
                      <div className="ml-[46px] h-px bg-black/[0.08]" />
                      <div className="px-4 py-[11px]">
                        <div className="flex items-center gap-2">
                          <Info className="w-3.5 h-3.5 text-brand-primary shrink-0" />
                          <span className="text-[13px] text-[#3C3C43]/60">
                            {costo === 1
                              ? 'Se consumirá 1 imagen'
                              : `Se consumirán ${costo} imágenes`}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* La espera NO se dibuja acá: va sobre la imagen, en la columna de al
                  lado (`CapaMejorando`). Duplicarla en el panel sería contar dos veces
                  lo mismo, y con dos barras distintas. */}
              {error && (
                <div className="rounded-lg bg-error-soft text-error-text text-[12.5px] px-3 py-2">
                  {error}
                </div>
              )}
              <div className="h-1" />
            </div>

            {/* El mismo pie que el recorte: el CTA a todo el ancho y la salida debajo. */}
            <div className="shrink-0 px-6 pt-3 pb-4 bg-white border-t border-black/[0.05] space-y-2">
              {mejorada ? (
                <button
                  type="button"
                  onClick={() => void descargar()}
                  className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-6 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover text-white text-[15.5px] font-semibold shadow-sm transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Descargar
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => void mejorar()}
                  disabled={procesando}
                  className="w-full inline-flex items-center justify-center gap-2.5 py-3.5 px-6 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[15.5px] font-semibold shadow-sm transition-colors"
                >
                  {procesando ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" /> Mejorando…
                    </>
                  ) : (
                    <>
                      <Maximize className="w-5 h-5" /> Mejorar
                    </>
                  )}
                </button>
              )}
              <button
                type="button"
                onClick={onVolver}
                disabled={procesando}
                className="w-full flex items-center justify-center h-11 rounded-2xl text-[15px] font-medium text-neutral-600 hover:bg-neutral-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {mejorada ? 'Elegir otra imagen' : 'Cancelar'}
              </button>
            </div>
          </>
        }
        muestra={
          <div className="flex-1 min-h-0 flex flex-col animate-paso-adelante">
            <div
              ref={columnaRef}
              className="flex-1 min-h-0 flex items-center justify-center"
            >
              {/* El marco EXACTO de la imagen: es contra esto que se posiciona la capa de
                  espera. Hasta tener las medidas no se dibuja nada —un instante— en vez
                  de mostrar la imagen a tamaño natural y después acomodarla. */}
              {caja.w > 0 && (
                <div className="relative" style={{ width: caja.w, height: caja.h }}>
                  {/* Las dos versiones se montan a la vez y se alternan con
                      `invisible`: así la mejorada ya está descargada cuando se toca
                      "Original" y volver no parpadea contra un <img> vacío.
                      Apiladas en `absolute inset-0` y NO con el atributo `hidden`: el
                      preflight de Tailwind declara `img { display: block }`, que le gana
                      al `[hidden] { display: none }` de la hoja del navegador, así que
                      ese atributo NO esconde imágenes en este proyecto — las dos se
                      dibujaban, una debajo de la otra. */}
                  <img
                    src={pick.signedUrl}
                    alt="Imagen original"
                    className={`absolute inset-0 w-full h-full object-contain rounded-2xl shadow-sm ${
                      mostrandoMejorada ? 'invisible' : ''
                    }`}
                  />
                  {mejorada && (
                    <img
                      src={mejorada}
                      alt="Imagen mejorada"
                      className={`absolute inset-0 w-full h-full object-contain rounded-2xl shadow-sm ${
                        mostrandoMejorada ? '' : 'invisible'
                      }`}
                    />
                  )}
                  {mostrandoEspera && (
                    <CapaMejorando
                      listo={!procesando}
                      onCompleto={() => setMostrandoEspera(false)}
                    />
                  )}
                </div>
              )}
            </div>
            {/* El comparador va DEBAJO DE LA IMAGEN y no en el panel: habla de lo que se
                está mirando, no de lo que se está por pedir. */}
            {mejorada && !mostrandoEspera && (
              <div className="shrink-0 pt-4 flex justify-center">
                <div className="inline-flex items-center gap-1 p-1 rounded-full bg-black/[0.06]">
                  {(['original', 'mejorada'] as const).map((cual) => (
                    <button
                      key={cual}
                      type="button"
                      onClick={() => setViendo(cual)}
                      className={`px-4 h-8 rounded-full text-[13px] font-medium transition-colors ${
                        viendo === cual
                          ? 'bg-white text-brand-black shadow-sm'
                          : 'text-neutral-600 hover:text-brand-black'
                      }`}
                    >
                      {cual === 'original' ? 'Original' : `Mejorada ${escala}x`}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        }
      />
      <SubscriptionLimitModal
        isOpen={limitModal !== null}
        type={limitModal ?? 'plan-limit-reached'}
        onClose={() => setLimitModal(null)}
      />
    </>
  );
};

/**
 * "Descripción", montada en el layout de los modos —
 * ADR-2026-09-08-la-descripcion-se-escribe-en-el-panel-del-modo.
 *
 * Mismo trato que el recorte y la mejora: la foto elegida se queda en la columna de la
 * derecha y el texto —que es lo que el usuario vino a buscar— se escribe en el panel de
 * la izquierda, en un campo editable con el CTA de copiar en el pie. Antes esto abría el
 * visor del historial: velo negro, la imagen en el medio y la descripción en un modal
 * encima de todo, o sea un contexto nuevo para el segundo paso del mismo modo.
 *
 * El visor NO se retira: sigue siendo el camino de mobile (`enMobile`) y el de quien
 * pide la descripción desde una imagen que ya tiene abierta, que es donde ese
 * tratamiento se pidió expresamente conservar.
 *
 * La descripción arranca sola al montar: elegir la imagen ES el pedido —no hay nada más
 * que configurar—, así que un botón "Generar" en el medio sería un paso de más.
 *
 * Componente aparte por lo mismo que los otros dos: todo su estado nace y muere con la
 * imagen elegida.
 */
const DescripcionEnElPanel: React.FC<{
  pick: GeneradaPick;
  /** Cómo se llama el atajo: encabeza la pantalla. */
  nombre: string;
  companyId: string | null;
  sidebarWidth: number;
  /**
   * Cambiar de imagen SIN volver al paso 1: lo elegido en el panel lateral pasa a ser
   * la imagen de esta pantalla, y todo el estado de acá nace de nuevo con ella (el
   * componente se remonta por `pick.id`).
   */
  onCambiarImagen: (pick: GeneradaPick) => void;
  onInicio: () => void;
}> = ({ pick, nombre, companyId, sidebarWidth, onCambiarImagen, onInicio }) => {
  const [panelImagenAbierto, setPanelImagenAbierto] = useState(false);
  const [generando, setGenerando] = useState(true);
  /** El texto, EDITABLE: se copia lo que quedó en el campo, no lo que vino del backend. */
  const [texto, setTexto] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);

  /**
   * El pedido, disparado al montar y una sola vez por imagen.
   *
   * Repite el orden del visor a propósito: primero se lee la fila —si esa imagen YA
   * tiene descripción se muestra esa y no se vuelve a pedir— y recién si está vacía se
   * llama al generador. Volver a describir una imagen ya descrita sería trabajo (y
   * consumo) por nada.
   */
  useEffect(() => {
    let cancelado = false;
    setGenerando(true);
    setTexto('');
    setError(null);
    (async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) throw new Error('No pudimos identificar tu cuenta. Recargá la página.');
        let idEmpresa = companyId;
        if (!idEmpresa) {
          const { data: perfil } = await supabase
            .from('profiles')
            .select('id_company')
            .eq('id', user.id)
            .maybeSingle();
          idEmpresa = (perfil as { id_company?: string | null } | null)?.id_company ?? null;
        }
        if (!idEmpresa) throw new Error('No pudimos identificar tu cuenta. Recargá la página.');

        const { data: fila } = await supabase
          .from('imagenes_generadas')
          .select('post_description')
          .eq('id', pick.id)
          .eq('id_company', idEmpresa)
          .maybeSingle();
        const yaEstaba = (fila as { post_description?: string | null } | null)
          ?.post_description;
        if (yaEstaba && yaEstaba.trim()) {
          if (!cancelado) setTexto(yaEstaba);
          return;
        }

        const res = await postInstagramDescription({
          user_id: user.id,
          image_id: pick.id,
        });
        if (!res.ok) throw new Error('No pudimos escribir la descripción. Intentá de nuevo.');
        // El atlas la devuelve en la respuesta; el poll de la fila es el camino de n8n.
        const final =
          res.postDescription ??
          (await esperarPostDescription({
            imageId: pick.id,
            companyId: idEmpresa,
            seguirEsperando: () => !cancelado,
          }));
        if (cancelado) return;
        if (final) setTexto(final);
        else
          setError(
            'La descripción todavía no está lista. Probá de nuevo en unos segundos.',
          );
      } catch (err) {
        if (!cancelado) {
          setError(
            err instanceof Error
              ? err.message
              : 'No pudimos escribir la descripción. Intentá de nuevo.',
          );
        }
      } finally {
        if (!cancelado) setGenerando(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [pick.id, companyId]);

  /**
   * El campo mide LO QUE MIDE EL TEXTO, no el alto que sobra en el panel.
   *
   * Un `<textarea>` no crece solo: hay que resetear el alto a `auto` —si no, el
   * `scrollHeight` arrastra el alto anterior y el campo sólo puede agrandarse— y
   * después ponerle su `scrollHeight`. Corre con cada cambio del texto: cuando llega la
   * descripción y cuando el usuario la edita.
   */
  const campoRef = useRef<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => {
    const el = campoRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [texto, generando]);

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      setError('No pudimos copiar la descripción. Copiala a mano desde el campo.');
    }
  };

  return (
    <ShellModoRedisenado
      sidebarWidth={sidebarWidth}
      migas={[{ label: 'Inicio', onClick: onInicio }, { label: nombre }]}
      titulo={nombre}
      panel={
        <>
          {/* La imagen elegida encabeza el panel con la MISMA card que Animar, Fotos de
              producto y Catálogo (`CardPicker`): miniatura, qué es y el chevron que
              anuncia que se puede cambiar. Reemplaza al "Elegir otra imagen" que estaba
              en el pie: era un botón de texto suelto debajo del CTA —el lugar de una
              salida, no el de un control— y para cambiar de foto había que volver al
              paso 1 y perder la descripción. */}
          <div className="shrink-0 px-6 pt-5 pb-1">
            <CardPicker
              etiqueta="Imagen"
              valor="De tus creaciones"
              imagenes={[pick.signedUrl]}
              onClick={() => setPanelImagenAbierto(true)}
            />
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-4 flex flex-col">
            <p className="text-[13px] text-[#3C3C43]/60 uppercase tracking-wide mb-2">
              Descripción del post
            </p>
            {/* La caja mide lo que mide el texto y no el alto que sobra: estirada al
                panel, una descripción de seis líneas dejaba media pantalla de blanco
                adentro de un recuadro. */}
            <div className="shrink-0 rounded-[13px] bg-white overflow-hidden border border-neutral-200 flex flex-col">
              {generando ? (
                // La misma barra que la sugerencia de Animar: el texto tarda ~15-20s y
                // un spinner solo, sin barra, se siente colgado. La animación es de
                // duración fija (el backend no reporta progreso) y `forwards` la deja
                // llena si tarda de más.
                <div className="px-4 py-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Loader2 className="w-5 h-5 text-brand-primary animate-spin" />
                    <span className="text-[15px] text-[#3C3C43]/60">
                      Generando la descripción…
                    </span>
                  </div>
                  <div className="w-full h-1 bg-black/[0.06] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#1644EB] rounded-full"
                      style={{ animation: 'descripcionProgress 20s linear forwards' }}
                    />
                  </div>
                  <style>{`@keyframes descripcionProgress { from { width: 0%; } to { width: 100%; } }`}</style>
                </div>
              ) : (
                <textarea
                  ref={campoRef}
                  rows={1}
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  placeholder="Acá va a aparecer la descripción de tu post."
                  className="w-full px-4 py-3 text-[15px] text-brand-black placeholder:text-[#3C3C43]/30 bg-transparent resize-none overflow-hidden focus:outline-none leading-relaxed"
                />
              )}
            </div>
            <p className="text-[13px] text-[#3C3C43]/60 mt-2">
              {generando
                ? 'Mira la imagen y arma el texto, con hashtags incluidos.'
                : 'Podés retocarla acá mismo antes de copiarla.'}
            </p>
            {error && (
              <div className="mt-3 rounded-lg bg-error-soft text-error-text text-[12.5px] px-3 py-2">
                {error}
              </div>
            )}
            <div className="h-1" />
          </div>

          {/* El pie es SÓLO el CTA: copiar es todo lo que queda por hacer con el texto, y
              cambiar de imagen se hace arriba, en la card. */}
          <div className="shrink-0 px-6 pt-3 pb-4 bg-white border-t border-black/[0.05]">
            <button
              type="button"
              onClick={() => void copiar()}
              disabled={generando || !texto.trim()}
              className={`w-full inline-flex items-center justify-center gap-2 py-3.5 px-6 rounded-2xl text-white text-[15.5px] font-semibold shadow-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                copiado
                  ? 'bg-success hover:bg-success'
                  : 'bg-brand-primary hover:bg-brand-primary-hover'
              }`}
            >
              {copiado ? (
                <>
                  <Check className="w-4 h-4" /> Copiada
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" /> Copiar descripción
                </>
              )}
            </button>
          </div>
        </>
      }
      // Cambiar la imagen sin salir de la pantalla: la misma capa con la que Animar,
      // Fotos de producto y Catálogo cambian lo suyo. Elegir cierra el panel y vuelve a
      // arrancar la descripción para la foto nueva.
      panelLateral={({ contenedorRef, offsetIzquierdo }) => (
        <PanelLateralModo
          abierto={panelImagenAbierto}
          onCerrar={() => setPanelImagenAbierto(false)}
          titulo="Elegí la imagen"
          offsetIzquierdo={offsetIzquierdo}
          contenedorRef={contenedorRef}
        >
          <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 pt-2">
            <p className="text-[13.5px] text-[#3C3C43]/60 leading-relaxed pb-3">
              Elegí otra de tus creaciones. El panel se cierra al elegir y Atlas escribe
              la descripción de esa foto.
            </p>
            <GeneradasPickGrid
              companyId={companyId}
              selectedId={pick.id}
              onSelect={(otra) => {
                setPanelImagenAbierto(false);
                onCambiarImagen(otra);
              }}
            />
          </div>
        </PanelLateralModo>
      )}
      muestra={
        // La foto descrita, centrada y sin caja propia: el marco lo lleva la imagen,
        // que es lo único que sabe cuánto mide.
        <div className="flex-1 min-h-0 flex items-center justify-center animate-paso-adelante">
          <img
            src={pick.signedUrl}
            alt="Imagen a describir"
            className="block max-h-full max-w-full w-auto h-auto object-contain rounded-2xl shadow-sm"
          />
        </div>
      }
    />
  );
};

/** ¿Es un slug de atajo conocido? */
export function esSlugAtajo(v: string | undefined): v is SlugAtajo {
  return !!v && v in ATAJOS;
}

export default function AtajoImagenMode() {
  const navigate = useNavigate();
  const { accion } = useParams<{ accion: string }>();
  const { sidebarWidth, setMobileBackOverride } = useSidebar();
  const volverAtras = useVolverAtras();
  // El paso 2 de recorte y de mejora es el layout de dos columnas, que abajo de `md` no
  // existe: ahí esas dos herramientas siguen abriendo el visor.
  const debajoDeMd = useDebajoDeMd();

  const config = esSlugAtajo(accion) ? ATAJOS[accion] : null;

  const [companyId, setCompanyId] = useState<string | null>(null);
  /** La imagen elegida. `null` = todavía estamos en el paso de elegir. */
  const [elegida, setElegida] = useState<GeneradaPick | null>(null);
  /** Mobile: la grilla vive en un sheet, así el panel de abajo no se llena. */
  const [sheetGeneradas, setSheetGeneradas] = useState(false);
  /** Desktop, sólo la expansión: el panel lateral donde se cambia la imagen sin salir
   *  del paso de formato. El mismo mecanismo de Animar, Carrusel y Fotos de producto. */
  const [panelImagenAbierto, setPanelImagenAbierto] = useState(false);
  /** La carátula declarada no cargó (el archivo todavía no está): se dibuja el ícono. */
  const [caratulaRota, setCaratulaRota] = useState(false);
  useEffect(() => setCaratulaRota(false), [accion]);
  const caratula = caratulaRota ? null : (config?.hero ?? null);

  // ── Sólo el atajo de expansión ──
  /**
   * La configuración con la que se volvió de generar, si el "Atrás" trajo una —
   * ADR-2026-09-10-los-modos-con-ruta-propia-tambien-vuelven-a-lo-que-generaste. El modo
   * vuelve al paso de formato con la imagen y el formato puestos, en vez de mandar a
   * elegir de nuevo la imagen que se acaba de expandir.
   */
  const configAlVolver = useRef(configDelModoAlMontar<ConfigDeExpansion>()).current;
  /** La fuente a expandir: fila generada O foto subida. Con esto puesto, la pantalla
   *  está en el paso de elegir formato. `url` es sólo para el preview. */
  const [fuenteExpansion, setFuenteExpansion] = useState<FuenteExpansion | null>(
    configAlVolver?.fuente ?? null,
  );
  const [expandiendo, setExpandiendo] = useState(false);
  const [limitModal, setLimitModal] = useState<
    'free-trial-ended' | 'plan-limit-reached' | null
  >(null);
  const [subiendo, setSubiendo] = useState(false);
  const [errorSubida, setErrorSubida] = useState<string | null>(null);
  const [errorExpansion, setErrorExpansion] = useState<string | null>(null);
  /** Medidas REALES de la fuente: definen qué formatos agregan lienzo y cuánto ocupa
   *  la original dentro del marco destino (misma mecánica que el sheet del visor). */
  const [medidas, setMedidas] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [errorMedidas, setErrorMedidas] = useState(false);
  const [ratio, setRatio] = useState<RatioExpansion | null>(configAlVolver?.ratio ?? null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Cada selección invalida a las anteriores en vuelo: si una subida vieja termina
  // tarde no puede pisar la imagen que el usuario está mirando (patrón del carrusel).
  const uploadSeqRef = useRef(0);

  /**
   * ¿Queda grilla abajo del scroll? Prende el degradado del pie del panel (desktop).
   *
   * Se mide en cada scroll y cada vez que CRECE el contenido: las filas las carga
   * `GeneradasPickGrid` en SU estado, así que esta pantalla no re-renderiza cuando
   * llegan y una medición atada al render se quedaría con la grilla vacía.
   */
  const scrollElegirRef = useRef<HTMLDivElement | null>(null);
  const contenidoElegirRef = useRef<HTMLDivElement | null>(null);
  const [quedaContenidoAbajo, setQuedaContenidoAbajo] = useState(false);
  const medirFinDelScroll = (el: HTMLDivElement | null) => {
    if (!el) return;
    // 4px de tolerancia: con zoom del browser `scrollTop` queda fraccionario y la resta
    // nunca da exactamente 0 al final.
    setQuedaContenidoAbajo(el.scrollHeight - el.scrollTop - el.clientHeight > 4);
  };
  useEffect(() => {
    const contenido = contenidoElegirRef.current;
    if (!contenido) return;
    const medir = () => medirFinDelScroll(scrollElegirRef.current);
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(contenido);
    window.addEventListener('resize', medir);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', medir);
    };
  }, [fuenteExpansion]);

  /** Vuelve del paso de formato al de elegir imagen (lo mismo que hace el carrusel). */
  const volverAElegir = () => {
    setFuenteExpansion(null);
    setErrorExpansion(null);
  };

  const opciones = opcionesParaImagen(medidas.w, medidas.h);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user || cancelado) return;
      const { data: p } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', auth.user.id)
        .maybeSingle();
      if (!cancelado) {
        setCompanyId((p as { id_company?: string | null } | null)?.id_company ?? null);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  // Un slug que no existe no muestra una pantalla rota: vuelve a la home.
  useEffect(() => {
    if (!config) navigate('/interfaz-v2', { replace: true });
  }, [config, navigate]);

  // Back flotante del shell en mobile, igual que Carrusel y Animar: además de dar el
  // botón, esconde la barra inferior (se apaga con `mobileBackOverride !== null`).
  // En el paso de formato de la expansión vuelve a elegir imagen, no a la home
  // (misma conducta que el `paso === 'configurar'` del carrusel).
  useEffect(() => {
    setMobileBackOverride(() => () => {
      if (expandiendo) return; // no se abandona una expansión en vuelo
      if (fuenteExpansion) volverAElegir();
      // Vuelve a donde estabas y no a un destino fijo —
      // ADR-2026-09-07-el-back-de-los-modos-vuelve-a-donde-estabas—. La home queda de
      // red, para cuando se entró por link directo y no hay nada atrás.
      else volverAtras();
    });
    return () => setMobileBackOverride(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fuenteExpansion, expandiendo, volverAtras]);

  // Las medidas se sacan de un preloader y NO del `onLoad` del <img> del preview: la
  // imagen elegida puede estar ya en caché y su `load` dispara antes de que React
  // enganche el handler — `complete` cubre ese caso. Sin `crossOrigin`, para no caer
  // en otra partición de caché y re-descargar (ADR-083). Misma jugada que el sheet
  // del visor.
  useEffect(() => {
    const url = fuenteExpansion?.url;
    if (!url) {
      setMedidas({ w: 0, h: 0 });
      setErrorMedidas(false);
      setRatio(null);
      return;
    }
    let cancelado = false;
    const medir = (img: HTMLImageElement) => {
      if (!cancelado && img.naturalWidth > 0) {
        setMedidas({ w: img.naturalWidth, h: img.naturalHeight });
      }
    };
    const img = new Image();
    img.onload = () => medir(img);
    // Sin medidas no hay herramienta: se dice, en vez de dejar "Cargando…" para
    // siempre (una URL firmada vencida entra justo por acá).
    img.onerror = () => {
      if (!cancelado) setErrorMedidas(true);
    };
    img.src = url;
    if (img.complete) medir(img);
    return () => {
      cancelado = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [fuenteExpansion?.url]);

  // Preselección: el primer formato que realmente expande. Recién cuando la imagen
  // cargó (antes no sabemos el encuadre actual).
  useEffect(() => {
    if (ratio !== null) return;
    const primera = opciones.find((o) => o.disponible);
    if (primera) setRatio(primera.id);
  }, [opciones, ratio]);

  if (!config) return null;

  /**
   * Saldo al ELEGIR la imagen, no al confirmar (mismo criterio que el visor: elegir
   * un formato para enterarse después de que no alcanzan los créditos es peor que no
   * abrir el panel). Falla ABIERTA: si la query no responde, el backend corta con 402.
   */
  const saldoAlcanza = async (costo = CREDITOS_EXPANSION): Promise<boolean> => {
    if (!companyId) return true;
    try {
      const { data } = await supabase
        .from('company_profiles')
        .select('plan, imagenes_restantes:imagenes_disponibles')
        .eq('id_company', companyId)
        .single();
      const restantes = (data as { imagenes_restantes?: number | null } | null)
        ?.imagenes_restantes;
      if (typeof restantes === 'number' && restantes < costo) {
        setLimitModal(
          (data as { plan?: string } | null)?.plan === 'gratis'
            ? 'free-trial-ended'
            : 'plan-limit-reached',
        );
        return false;
      }
    } catch {
      /* falla abierta */
    }
    return true;
  };

  const alElegir = (pick: GeneradaPick) => {
    setSheetGeneradas(false);
    setPanelImagenAbierto(false);
    if (config.destino.tipo === 'expansion') {
      void (async () => {
        if (await saldoAlcanza()) {
          // El formato se REINICIA con la imagen: qué encuadres agregan lienzo depende
          // de las medidas de la fuente (`opcionesParaImagen`), así que arrastrar el
          // elegido para la anterior podía dejar marcado uno que para ésta no expande.
          // Con `null`, la preselección vuelve a correr sobre las medidas nuevas.
          setRatio(null);
          setFuenteExpansion({ imagenId: pick.id, url: pick.signedUrl });
        }
      })();
      return;
    }
    if (config.destino.tipo === 'ruta' && !(config.destino.enDesktop && !debajoDeMd)) {
      if (!companyId) return;
      navigate(config.destino.armar(pick.id, companyId));
      return;
    }
    if (config.destino.tipo === 'upscale' && !debajoDeMd) {
      // Mismo criterio que la expansión: el saldo se chequea al ELEGIR y no al
      // confirmar. Se mide contra 1 imagen, el costo de la escala 2x con la que abre el
      // paso; si después elige 4x y no le alcanza, corta el backend y la pantalla
      // levanta ese `insufficient_credits` con el mismo modal.
      void (async () => {
        if (await saldoAlcanza(1)) setElegida(pick);
      })();
      return;
    }
    setElegida(pick);
  };

  const elegirArchivo = () => fileInputRef.current?.click();

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permitir re-elegir el mismo archivo
    if (!file) return;
    if (!companyId) {
      setErrorSubida('No pudimos identificar tu cuenta. Recargá la página.');
      return;
    }
    const seq = ++uploadSeqRef.current;
    setErrorSubida(null);
    setSubiendo(true);
    try {
      const subida = await subirImagenParaAnimar(file, companyId, 'expansion');
      if (seq !== uploadSeqRef.current) return; // ganó una selección más nueva
      if (await saldoAlcanza()) {
        setPanelImagenAbierto(false);
        // Formato nuevo para imagen nueva — mismo motivo que en `alElegir`.
        setRatio(null);
        setFuenteExpansion({
          subida: { bucket: subida.bucket, path: subida.path },
          url: subida.previewUrl,
        });
      }
    } catch (err) {
      if (seq !== uploadSeqRef.current) return; // el fallo es de una subida vieja
      setErrorSubida(
        err instanceof Error ? err.message : 'No se pudo subir la imagen.',
      );
    } finally {
      if (seq === uploadSeqRef.current) {
        setSubiendo(false);
        // El sheet de mobile se cierra al TERMINAR la subida, con o sin éxito —
        // ADR-2026-09-08-la-subida-de-mobile-cierra-el-sheet. Desde que "Subir imagen" es
        // una celda de esa grilla (y no un botón en la pantalla), quien la dispara está
        // ADENTRO del sheet: sin esto la subida corría, la pantalla de atrás avanzaba al
        // paso 2 y el usuario seguía mirando la misma lista, como si no hubiera pasado
        // nada. Y si falla, el aviso que se dibuja abajo tampoco se veía.
        setSheetGeneradas(false);
      }
    }
  };

  const expandir = async () => {
    if (!fuenteExpansion || !companyId || expandiendo || !ratio) return;
    setExpandiendo(true);
    setErrorExpansion(null);
    try {
      const nueva = await expandirImagen({
        imagenId: fuenteExpansion.imagenId,
        subida: fuenteExpansion.subida,
        aspectRatio: ratio,
        companyId,
        modoUtilizado: 'expansion_magica_atajo',
      });
      // La vuelta atrás: el "Atrás" desde los resultados devuelve ESTA configuración, no
      // la pantalla de elegir imagen. Va antes del `navigate` de abajo, que es el que
      // apila la pantalla de resultados sobre esta entrada ya sellada.
      recordarConfigDelModo<ConfigDeExpansion>(navigate, `/atajo/${accion}`, {
        fuente: fuenteExpansion,
        ratio,
      });
      // La galería de recién generadas (GeneratedImagesInline), igual que el carrusel.
      // La tanda es la imagen nueva sola, ya firmada por `expandirImagen` — va
      // precargada y el inline no tiene que pollear nada.
      navigate('/redes-sociales/imagenes-generadas', {
        state: {
          imageIds: [nueva.id],
          companyId,
          preloadedImages: [
            {
              id: nueva.id,
              fileName: nueva.fileName,
              signedUrl: nueva.signedUrl,
              favorito: false,
            },
          ],
        },
      });
    } catch (error) {
      if (error instanceof GenerateAdError && error.errorType === 'billing') {
        setLimitModal('plan-limit-reached');
      } else {
        setErrorExpansion(
          error instanceof Error
            ? error.message
            : 'No pudimos procesar la imagen. Intentá nuevamente en unos minutos.',
        );
      }
    } finally {
      try {
        window.dispatchEvent(new Event('atlas:credits-updated'));
      } catch {
        /* sin listeners no pasa nada */
      }
      setExpandiendo(false);
    }
  };

  // ── Paso 2 ────────────────────────────────────────────────────────────────
  //
  // El visor del historial, prestado, con la acción ya disparada. Al cerrarlo se vuelve
  // al paso de elegir y no a la home: quien entró por "Mejorar" probablemente quiera
  // mejorar otra. Lo usan los destinos `visor`, Editar en escritorio (la barra de Editar
  // del visor, ADR-2026-10-07-el-atajo-editar-abre-el-visor) y —sólo en mobile— el recorte y la
  // mejora, que arriba de `md` tienen su paso propio en esta pantalla.
  const accionEnElVisor: AccionVisor | null = (() => {
    const d = config.destino;
    if (d.tipo === 'visor') return d.accion;
    if (d.tipo === 'ruta') return (!debajoDeMd && d.enDesktop) || null;
    if (debajoDeMd && (d.tipo === 'recorte' || d.tipo === 'upscale' || d.tipo === 'descripcion')) return d.enMobile;
    return null;
  })();

  if (elegida && accionEnElVisor) {
    return (
      <GeneratedImagesHistory
        soloVisor={{
          imageId: elegida.id,
          accionInicial: accionEnElVisor,
          onCerrar: () => setElegida(null),
        }}
      />
    );
  }

  // Recorte y mejora, en ESTA pantalla y con el layout de los modos: los controles en el
  // panel de la izquierda y la imagen a la derecha.
  if (elegida && config.destino.tipo === 'recorte') {
    return (
      <RecorteEnElPanel
        pick={elegida}
        nombre={config.nombre}
        sidebarWidth={sidebarWidth}
        onVolver={() => setElegida(null)}
        onInicio={() => navigate('/interfaz-v2')}
      />
    );
  }

  if (elegida && config.destino.tipo === 'descripcion') {
    return (
      <DescripcionEnElPanel
        // Remonta con la imagen: el texto, el error y el "Copiada" son de ESA foto, y
        // arrastrarlos a la siguiente mostraría la descripción de la anterior mientras
        // se escribe la nueva.
        key={elegida.id}
        pick={elegida}
        nombre={config.nombre}
        companyId={companyId}
        sidebarWidth={sidebarWidth}
        onCambiarImagen={setElegida}
        onInicio={() => navigate('/interfaz-v2')}
      />
    );
  }

  if (elegida && config.destino.tipo === 'upscale') {
    return (
      <MejorarEnElPanel
        pick={elegida}
        nombre={config.nombre}
        companyId={companyId}
        sidebarWidth={sidebarWidth}
        onVolver={() => setElegida(null)}
        onInicio={() => navigate('/interfaz-v2')}
      />
    );
  }

  const { Icono } = config;

  // ── Paso "elegir formato" de la expansión (ocupa ESTA pantalla, no un sheet) ──
  const enConfigurarExpansion =
    config.destino.tipo === 'expansion' && fuenteExpansion !== null;
  const marco = ratio
    ? opciones.find((o) => o.id === ratio)!
    : { id: '4:5' as RatioExpansion, etiqueta: '', w: 4, h: 5, disponible: false };
  const { anchoPct, altoPct } = ratio
    ? encuadreOriginal(ratio, medidas.w, medidas.h)
    : { anchoPct: 1, altoPct: 1 };
  const crece = ratio ? ladoQueCrece(ratio, medidas.w, medidas.h) : null;
  const listoParaExpandir = ratio !== null && medidas.w > 0 && !expandiendo;

  /**
   * El preview de la expansión, como hero del paso: el marco DESTINO con la original
   * nítida del tamaño exacto que va a ocupar, sobre la misma imagen borroneada como
   * volumen de lo que la IA completa. Es el dibujo del sheet del visor, en grande.
   * `maxAlto` manda: el ancho sale del ratio del marco (con tope para los apaisados).
   */
  const previewExpansion = (maxAlto: string) =>
    fuenteExpansion ? (
      <div
        className="relative overflow-hidden rounded-2xl bg-neutral-100 shadow-sm"
        style={{
          // `aspect-[w/h]` de Tailwind no compila en este proyecto: va inline.
          aspectRatio: `${marco.w} / ${marco.h}`,
          width: `min(92%, 480px, calc(${maxAlto} * ${marco.w} / ${marco.h}))`,
          transition: 'aspect-ratio 300ms ease-out, width 300ms ease-out',
        }}
      >
        <img
          src={fuenteExpansion.url}
          alt=""
          aria-hidden
          className={`absolute inset-0 w-full h-full object-cover blur-xl ${expandiendo ? 'expansion-latido' : 'opacity-80'}`}
          style={{ transform: 'scale(1.25)' }}
        />
        <img
          src={fuenteExpansion.url}
          alt="Vista previa de la expansión"
          className="absolute left-1/2 top-1/2 object-contain"
          style={{
            width: `${anchoPct * 100}%`,
            height: `${altoPct * 100}%`,
            transform: 'translate(-50%, -50%)',
            transition: 'width 300ms ease-out, height 300ms ease-out',
            boxShadow: '0 0 0 1px rgba(255,255,255,0.65)',
          }}
        />
        {/* Brillo que barre el marco mientras corre la generación. */}
        {expandiendo && (
          <div
            aria-hidden
            className="absolute inset-y-0 -left-1/3 w-1/3 expansion-barrido pointer-events-none"
            style={{
              background:
                'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.45) 50%, rgba(255,255,255,0) 100%)',
            }}
          />
        )}
      </div>
    ) : null;

  /** Contenido de la card en el paso de formato: selector + info + progreso. */
  const bloqueConfigurarExpansion = (
    <>
      <div className="flex items-start gap-3.5 mb-4">
        <div className="w-11 h-11 rounded-xl bg-[#1644EB]/10 flex items-center justify-center flex-shrink-0">
          <Expand className="w-5 h-5 text-brand-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[16px] font-semibold text-brand-black leading-tight">
            Elegí el formato
          </p>
          <p className="text-[13.5px] text-[#3C3C43]/60 mt-1 leading-relaxed">
            Tu foto queda intacta: la IA pinta el lienzo que falta alrededor.
          </p>
        </div>
      </div>

      {/* El mismo selector del sheet del visor: 4 miniaturas de encuadre. */}
      <div>
        <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
          Formato
        </p>
        <div className="grid grid-cols-4 gap-2">
          {opciones.map((op) => {
            const activo = op.id === ratio;
            return (
              <button
                key={op.id}
                type="button"
                disabled={!op.disponible || expandiendo}
                onClick={() => setRatio(op.id)}
                title={
                  op.disponible
                    ? op.etiqueta
                    : medidas.w === 0
                      ? 'Todavía estamos leyendo la imagen'
                      : 'La imagen ya tiene este formato'
                }
                className={`flex flex-col items-center justify-center gap-1.5 py-3 rounded-lg border transition-colors ${
                  activo
                    ? 'border-[#1644EB] bg-[#EEF2FE]'
                    : 'border-neutral-200 bg-white hover:bg-neutral-50'
                } ${!op.disponible || expandiendo ? 'opacity-40 cursor-not-allowed hover:bg-white' : ''}`}
              >
                {/* Miniatura del encuadre: 22px de lado mayor. */}
                <span
                  className={`block rounded-[3px] border-2 ${activo ? 'border-[#1644EB]' : 'border-neutral-400'}`}
                  style={{
                    width: op.w >= op.h ? 22 : (22 * op.w) / op.h,
                    height: op.h >= op.w ? 22 : (22 * op.h) / op.w,
                  }}
                />
                <span
                  className={`text-[11px] font-medium ${activo ? 'text-[#1644EB]' : 'text-brand-black/70'}`}
                >
                  {op.id}
                </span>
              </button>
            );
          })}
        </div>
        <p className="text-[13px] text-[#3C3C43]/60 mt-2">
          {errorMedidas
            ? 'No pudimos leer la imagen. Volvé atrás y elegí otra.'
            : medidas.w === 0
              ? 'Cargando la imagen…'
              : crece
                ? `La IA completa ${crece === 'costados' ? 'los costados' : 'arriba y abajo'} sin recortar nada de la foto.`
                : 'Elegí un formato distinto al actual.'}
        </p>
      </div>

      <div className="mt-4">
        <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
          Información
        </p>
        <div className="rounded-[13px] bg-white overflow-hidden border border-neutral-200">
          <div className="px-4 py-[11px]">
            <div className="flex items-center gap-3 mb-2">
              <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">
                expand_content
              </span>
              <span className="text-[15px] text-brand-black/90">
                Se guarda como imagen nueva
              </span>
            </div>
            <p className="text-[13px] text-[#3C3C43]/60">
              La original queda intacta en tu historial: la expandida se suma como una
              imagen aparte.
            </p>
          </div>
          <div className="ml-[46px] h-px bg-black/[0.08]" />
          <div className="px-4 py-[11px]">
            <div className="flex items-center gap-2">
              <Info className="w-3.5 h-3.5 text-[#1644EB] shrink-0" />
              <span className="text-[13px] text-[#3C3C43]/60">
                {CREDITOS_EXPANSION === 1
                  ? 'Se consumirá 1 imagen'
                  : `Se consumirán ${CREDITOS_EXPANSION} imágenes`}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Estado "expandiendo": misma barra que el sheet del visor (gris → primario,
          ~40s, sin porcentaje falso — la expansión suele resolverse antes). */}
      {expandiendo && (
        <div className="mt-4" role="status" aria-live="polite">
          <div className="flex items-center gap-2 mb-2">
            <Loader2 className="w-4 h-4 animate-spin text-brand-primary" />
            <span className="text-[13px] text-brand-black/80">
              Completando la escena…
            </span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden bg-neutral-200">
            <div
              className="h-full rounded-full"
              style={{
                width: '100%',
                transformOrigin: 'left',
                animation:
                  'expansion-progreso 40s cubic-bezier(0, 0, 0.2, 1) forwards',
                background:
                  'linear-gradient(to right, #D1D5DB 10%, var(--brand-primary) 70%)',
              }}
            />
          </div>
        </div>
      )}
      {errorExpansion && (
        <div className="mt-3 rounded-lg bg-error-soft text-error-text text-[12.5px] px-3 py-2">
          {errorExpansion}
        </div>
      )}
    </>
  );

  /** El CTA del paso de formato. Lo comparten los dos pies —el de mobile y el del
   *  panel—: lo único que cambia entre ellos es el aire alrededor. */
  const botonExpandir = (
    <button
      type="button"
      onClick={() => void expandir()}
      disabled={!listoParaExpandir}
      className="w-full inline-flex items-center justify-center gap-2.5 py-3.5 px-6 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[15.5px] font-semibold shadow-sm transition-colors"
    >
      {expandiendo ? (
        <>
          <Loader2 className="w-5 h-5 animate-spin" /> Expandiendo…
        </>
      ) : (
        <>
          <Expand className="w-5 h-5" /> Expandir
        </>
      )}
    </button>
  );

  /** Pie de MOBILE: sticky al fondo del panel blanco de abajo. */
  const pieExpandirMobile = (
    <div className="shrink-0 border-t border-black/[0.06] bg-white px-5 pt-3 pb-5">
      {botonExpandir}
    </div>
  );

  /** Pie del PANEL de desktop — el mismo que el de los otros modos del rediseño. */
  const pieExpandirPanel = (
    <div className="shrink-0 px-6 pt-3 pb-4 bg-white border-t border-black/[0.05]">
      {botonExpandir}
    </div>
  );

  // ── Piezas del DESKTOP: el diseño de Automática / Modelo / Catálogo ───────

  /**
   * "Subir imagen" como PRIMERA CELDA de la grilla, no como botón arriba de ella. Sólo
   * en el atajo que sabe recibir una foto propia (hoy: expansión); en los otros cuatro
   * la grilla arranca directo con las generadas, que es lo único que sus backends
   * aceptan.
   *
   * El chip del ícono va en hex con alpha y no en `bg-brand-primary/10`: el token no
   * tiene `<alpha-value>` y el modificador no emite ninguna regla.
   */
  const celdaSubirImagen = (
    <button
      type="button"
      onClick={elegirArchivo}
      disabled={subiendo}
      className="relative aspect-square rounded-[13px] overflow-hidden bg-white border-2 border-dashed border-neutral-300 hover:border-brand-primary hover:bg-[#1644EB]/[0.03] disabled:opacity-60 disabled:cursor-not-allowed active:scale-[0.98] transition-all flex flex-col items-center justify-center gap-2 px-2"
    >
      <span className="w-9 h-9 rounded-xl bg-[#1644EB]/10 flex items-center justify-center">
        {subiendo ? (
          <Loader2 className="w-5 h-5 text-brand-primary animate-spin" />
        ) : (
          <ImageUp className="w-5 h-5 text-brand-primary" />
        )}
      </span>
      <span className="text-[11.5px] font-bold text-brand-primary text-center leading-tight">
        {subiendo ? 'Subiendo…' : 'Subir imagen'}
      </span>
    </button>
  );

  /**
   * La grilla del atajo. La MISMA en los dos lugares donde se elige la imagen: el paso
   * de elegir y —en la expansión— el panel lateral del paso de formato.
   */
  const grillaDeImagenes = (
    <GeneradasPickGrid
      companyId={companyId}
      // En el paso de formato la que está en uso va tildada: el panel se abre para
      // cambiarla, así que tiene que decir cuál es la de ahora.
      selectedId={fuenteExpansion?.imagenId ?? null}
      onSelect={alElegir}
      tileInicial={config.conSubirPropia ? celdaSubirImagen : undefined}
      emptyHint={config.emptyHint}
    />
  );

  /** El paso de elegir, en el panel: el set de imágenes del que sale la del atajo. */
  const panelElegir = (
    <>
      {/* UNA sola línea, en la misma medida que la de los otros modos: la consigna. Lo
          que el atajo hace con la imagen lo cuenta el copy de la muestra, al lado —
          antes estaba en las dos partes. */}
      <div className="shrink-0 px-6 flex justify-between gap-2 items-baseline pt-5 pb-1">
        <p className="text-[17px] font-semibold text-brand-black leading-tight">
          {config.titulo}
        </p>
      </div>
      <div className="relative flex-1 min-h-0 flex flex-col">
        <div
          ref={scrollElegirRef}
          onScroll={(e) => medirFinDelScroll(e.currentTarget)}
          className="flex-1 min-h-0 overflow-y-auto px-6 pt-4"
        >
          <div ref={contenidoElegirRef}>
            {grillaDeImagenes}
            {errorSubida && (
              <div className="mt-3 rounded-lg bg-error-soft text-error-text text-[12.5px] px-3 py-2">
                {errorSubida}
              </div>
            )}
            <div className="h-3" />
          </div>
        </div>
        {/* Degradado del pie: la última fila se desvanece en vez de cortarse contra el
            borde. Se apaga al llegar abajo — si quedara siempre, dejaría de significar
            "hay más". */}
        {quedaContenidoAbajo && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-white via-white/85 to-transparent" />
        )}
      </div>
    </>
  );

  /** El paso de formato de la expansión, en el panel. */
  const panelConfigurarExpansion = (
    <>
      {/* La imagen elegida encabeza la configuración con la MISMA card con la que Fotos
          de producto y Catálogo encabezan la suya con los productos. Tocarla abre el
          panel lateral: cambiar de imagen ya no es volver al paso anterior. */}
      <div className="shrink-0 px-6 pt-5 pb-1">
        <CardPicker
          etiqueta="Imagen"
          valor={fuenteExpansion?.subida ? 'Foto que subiste' : 'De tus creaciones'}
          imagenes={fuenteExpansion ? [fuenteExpansion.url] : []}
          onClick={() => setPanelImagenAbierto(true)}
        />
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-3 pb-4">
        {bloqueConfigurarExpansion}
      </div>
      {pieExpandirPanel}
    </>
  );

  /** La carátula del atajo, o el ícono grande cuando todavía no hay arte propio. */
  const heroImg = (className: string) =>
    caratula ? (
      <img
        src={caratula}
        alt=""
        className={className}
        // Las carátulas de los cuatro atajos sin arte propio están declaradas pero
        // todavía no existen en `public/`: si el archivo no está, esto cae al ícono en
        // vez de dejar la imagen rota.
        onError={() => setCaratulaRota(true)}
      />
    ) : (
      // Mismo lenguaje que el chip de la card, en grande: cuadrado redondeado con el
      // tinte de marca. No es un placeholder gris — se lee como una pieza más del
      // sistema mientras no exista la carátula real.
      //
      // Mide lo suyo y NO toma el `className` de la carátula real: ése lleva un ancho y
      // un alto máximos distintos (560×480, las proporciones del arte), y contra un
      // `aspect-square` daban una caja apaisada — o sea, un cuadrado que no era cuadrado.
      <div className="grid h-[420px] w-[420px] max-h-full max-w-full place-items-center rounded-2xl bg-[#1644EB]/[0.07]">
        <Icono className="h-16 w-16 text-brand-primary" strokeWidth={1.4} />
      </div>
    );

  /**
   * MOBILE con la anatomía de Modelo — ADR-2026-09-08-todos-los-modos-abren-igual-en-mobile.
   *
   * La card abre el MISMO sheet que abría el CTA de antes. En la expansión —el único
   * atajo que acepta una foto propia— "Subir imagen" pasó adentro de ese sheet, como
   * primera celda de la grilla: con una sola card no hay dónde poner un segundo botón, y
   * ese es el lugar que la celda ya ocupa en el panel de escritorio.
   */
  const pantallaMobileComoModelo = (
    <PasoUnoMobile
      titulo={config.titulo}
      bajada={config.bajada}
      portada={heroImg('max-w-[400px] max-h-full w-full h-auto object-contain')}
      Icono={Icono}
      cardTexto="Elegí tu imagen"
      onAbrir={() => setSheetGeneradas(true)}
      aviso={
        errorSubida ? (
          <div className="mt-3 rounded-lg bg-error-soft text-error-text text-[12.5px] px-3 py-2">
            {errorSubida}
          </div>
        ) : null
      }
    />
  );

  return (
    <>
      {/* ── MOBILE ────────────────────────────────────────────────────────── */}
      {!enConfigurarExpansion ? (
        pantallaMobileComoModelo
      ) : (
      /* PASO 2 DE LA EXPANSIÓN: carátula full-bleed + panel blanco redondeado abajo. Es
         el único paso de estos modos que sigue con ese layout — elegir el formato es
         mirar el preview del encuadre, no elegir de una lista. */
      <div className="md:hidden fixed inset-0 z-30 bg-surface-mobile flex flex-col">
        <div className="relative flex-1 min-h-0 bg-neutral-100">
          {enConfigurarExpansion ? (
            // El preview del encuadre destino ocupa el lugar de la carátula. El tope
            // de alto deja aire para el -mt-6 del panel de abajo.
            <div className="absolute inset-0 flex items-center justify-center p-4 pb-10">
              {previewExpansion('34dvh')}
            </div>
          ) : caratula ? (
            <img
              src={caratula}
              alt=""
              className="absolute inset-0 w-full h-full object-contain p-6"
              onError={() => setCaratulaRota(true)}
            />
          ) : (
            <div className="absolute inset-0 grid place-items-center">
              <Icono className="h-20 w-20 text-brand-primary/70" strokeWidth={1.3} />
            </div>
          )}
        </div>
        <div
          className="relative z-10 bg-white shrink-0 -mt-6 rounded-t-3xl flex flex-col overflow-hidden"
          style={{ maxHeight: '55dvh' }}
        >
          <div className="flex-1 min-h-0 overflow-y-auto px-5 pt-7 pb-6">
            {bloqueConfigurarExpansion}
          </div>
          {pieExpandirMobile}
        </div>
      </div>
      )}

      {/* ── DESKTOP: el diseño de los modos — panel a la izquierda, muestra a la
          derecha (ADR-2026-09-07-los-atajos-toman-el-diseno-de-los-modos) ────── */}
      <ShellModoRedisenado
        sidebarWidth={sidebarWidth}
        // Los mismos dos niveles que arman los modos: la home y dónde estás. El nombre
        // sale de `config.nombre`, que es el mismo `label` de "Ediciones rápidas".
        migas={[
          { label: 'Inicio', onClick: () => navigate('/interfaz-v2') },
          { label: config.nombre },
        ]}
        titulo={config.nombre}
        panel={enConfigurarExpansion ? panelConfigurarExpansion : panelElegir}
        muestra={
          <div
            // `key` por paso: la columna se remonta y hace su entrada, igual que en los
            // otros modos al cambiar de paso.
            key={enConfigurarExpansion ? 'muestra-encuadre' : 'muestra-hero'}
            className="flex-1 min-h-0 flex flex-col items-center justify-center gap-2 animate-paso-adelante"
          >
            {enConfigurarExpansion ? (
              <>
                {previewExpansion('58vh')}
                <p className="text-[#3C3C43]/60 leading-relaxed text-center text-balance text-[15.5px] max-w-[440px] mt-1">
                  Tu foto, del tamaño exacto que va a ocupar en el encuadre nuevo.
                </p>
              </>
            ) : (
              <>
                {/* El texto ENCABEZA la muestra en vez de ir de pie de foto — el mismo
                    tratamiento de Modelo v2 y Catálogo
                    (ADR-2026-08-21-modelo-v2-la-muestra-se-queda): título en negrita
                    arriba y la bajada debajo, para que se lea como el título de lo que
                    el modo hace y no como el epígrafe de la imagen. */}
                <div className="shrink-0 text-center max-w-[440px] mb-3">
                  <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black/90">
                    {config.heroTitulo}
                  </h2>
                  <p className="text-[15.5px] text-[#3C3C43]/60 leading-relaxed mt-1">
                    {config.heroDesc}
                  </p>
                </div>
                {heroImg(
                  'max-w-[560px] max-h-[480px] w-auto h-auto object-contain rounded-2xl',
                )}
              </>
            )}
          </div>
        }
        // Cambiar la imagen sin salir del paso de formato. Sólo la expansión lo tiene:
        // es el único atajo con un segundo paso en esta pantalla.
        panelLateral={({ contenedorRef, offsetIzquierdo }) => (
          <PanelLateralModo
            abierto={enConfigurarExpansion && panelImagenAbierto}
            onCerrar={() => setPanelImagenAbierto(false)}
            titulo="Elegí la imagen"
            offsetIzquierdo={offsetIzquierdo}
            contenedorRef={contenedorRef}
          >
            <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 pt-2">
              <p className="text-[13.5px] text-[#3C3C43]/60 leading-relaxed pb-3">
                Subí una foto nueva o elegí otra de tus creaciones. El panel se cierra al
                elegir.
              </p>
              {grillaDeImagenes}
            </div>
          </PanelLateralModo>
        )}
      />

      {/* Mobile: la grilla en un bottom sheet, igual que el carrusel. */}
      <OptionSheet
        open={sheetGeneradas}
        onClose={() => setSheetGeneradas(false)}
        title="Mis imágenes generadas"
        hideAccept
      >
        {/* La MISMA grilla del panel de escritorio: con la celda de "Subir imagen"
            primera en los atajos que aceptan una foto propia. Antes ese camino era un
            botón aparte en el panel de abajo, que la pantalla nueva ya no tiene. */}
        {grillaDeImagenes}
      </OptionSheet>

      {/* Sólo expansión: el input de la foto propia. */}
      {config.destino.tipo === 'expansion' && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onFile}
        />
      )}

      {/* El modal del chequeo de saldo, que corre al ELEGIR la imagen. Lo comparten los
          dos atajos que cobran antes de empezar: la expansión y la mejora. */}
      <SubscriptionLimitModal
        isOpen={limitModal !== null}
        type={limitModal ?? 'plan-limit-reached'}
        onClose={() => setLimitModal(null)}
      />
    </>
  );
}
